import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { Field, FieldArray } from 'redux-form';
import { IStore } from 'redux/IStore';
import { FieldArrayFieldsProps, InjectedFormProps, ITranslateProps } from 'models/common';
import { IBookingTimeTableForm, IBookingTimeTablePeriod } from 'models/shop/bookingtimetables';
import { PortalCheckboxField, BookingDatePicker, BookingSelectMenu } from 'components/FormControls';
import { FieldArrayMetaProps } from 'models/common/redux-form';
import { createEndTimeValidationFuntion, required } from 'helpers/validationattributes';
import { timePickerProps } from 'helpers/common';
import renderIcon from 'helpers/renderIcon';
import { CustomerCheckboxField } from 'containers/Shop/ShopBundlesServicesAddEdit/ShopServicesSingleAddEdit/CustomerCheckboxField';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
const { connect } = require('react-redux');

/* Implementation of lodash.get function */
function getProp(object: any, keys: string | string[]) {
	keys = Array.isArray(keys) ? keys : keys.split('.');
	object = object[keys[0]];
	if (object && keys.length > 1) {
		return getProp(object, keys.slice(1));
	}
	return object;
}

interface IOwnProps extends Pick<InjectedFormProps<IBookingTimeTableForm>, 'array' | 'submitting' | 'change'> {
	rootKey: number;
	dayLabel?: string | JSX.Element;
	minTime: Date | null;
	maxTime: Date | null;
	initialMaxTime?: Date | null;
	initialMinTime?: Date | null;
	weekIndexSelected?: number;
	timezone: string;
}

interface IProps extends IOwnProps, ITranslateProps, ReturnType<typeof mapStateToProps> {}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class WorkDayPeriodForm extends React.Component<IProps> {
	public constructor(props: IProps) {
		super(props);

		this.renderPeriods = this.renderPeriods.bind(this);
		this.renderPeriod = this.renderPeriod.bind(this);
	}

	public componentDidUpdate(prevProps: Readonly<IProps>): void {
		const wasActive = getProp(prevProps.currentFormValues, this.activeKey);
		const hadPeriods = getProp(prevProps.currentFormValues, this.periodsKey)?.length;
		const hasPeriods = getProp(this.props.currentFormValues, this.periodsKey)?.length;

		if (this.isActive && !wasActive && !this.periods.length) {
			if (!this.periods.length && !this.isDisabled) {
				this.props.array.push(this.periodsKey, { start: '', end: '' });
			} else if (this.periods.length && this.isDisabled) {
				this.props.array.removeAll(this.periodsKey);
			}
		}

		if (this.isActive && ((hadPeriods && !this.periods.length) || this.isDisabled)) {
			this.props.change(this.activeKey, false);
		}

		try {
			let i: string = 'workTime.' + this.props.rootKey.toString();

			if (this.props.weekIndexSelected === 0 || this.props.weekIndexSelected) {
				i = `workTime.${this.props.weekIndexSelected}.` + this.props.rootKey.toString();
			}

			// if a new interval was added set the minimum start time and maximum end time
			if (!hadPeriods && hasPeriods) {
				this.props.change(`${i}.periods[0].startTime`, this.props.initialMinTime || this.props.minTime);
				this.props.change(`${i}.periods[0].endTime`, this.props.initialMaxTime || this.props.maxTime);
				this.props.change(`${i}.periods[0].availabilityType`, 0);
			}
		} catch (e) {
			console.error(e);
		}
	}

	public componentDidMount(): void {
		if (this.isDisabled && this.periods.length) {
			this.props.array.removeAll(this.periodsKey);
		}
	}

	private availabilityItems = () => {
		const { t } = this.props;
		return [
			{ key: 0, value: t('Shop_WorkTime_OnlineManual_Booking') },
			{ key: 1, value: t('Shop_WorkTime_Manual_Booking') },
		];
	};

	public render() {
		const { dayLabel } = this.props;
		if (dayLabel) {
			return this.renderAsDay();
		}
		return this.renderAsChoice();
	}

	public get activeKey(): string {
		if (this.props.weekIndexSelected === 0 || this.props.weekIndexSelected) {
			return `workTime.${this.props.weekIndexSelected}.` + this.props.rootKey + '.active';
		} else {
			return `workTime.` + this.props.rootKey + '.active';
		}
	}

	public get isActive(): boolean {
		const { currentFormValues } = this.props;

		return Boolean(getProp(currentFormValues, this.activeKey));
	}

	public get isDisabled(): boolean {
		const { minTime, maxTime } = this.props;
		return minTime === null || maxTime === null;
	}

	public get periodsKey(): string {
		if (this.props.weekIndexSelected === 0 || this.props.weekIndexSelected) {
			return `workTime.${this.props.weekIndexSelected}.` + this.props.rootKey + '.periods';
		} else {
			return 'workTime.' + this.props.rootKey + '.periods';
		}
	}

	public get periods(): IBookingTimeTablePeriod[] {
		const { currentFormValues } = this.props;
		const periods = getProp(currentFormValues, this.periodsKey);
		return Array.isArray(periods) ? periods : [];
	}

	public get canAdd(): boolean {
		if (!this.isActive) {
			return false;
		}
		return !this.periods.some((p) => !p.startTime || !p.endTime);
	}

	// For week edit
	public renderAsDay() {
		const { dayLabel } = this.props;
		let times: JSX.Element = null;

		if (this.isActive) {
			times = (
				<div className="col-12">
					<FieldArray name={this.periodsKey} component={this.renderPeriods} />
				</div>
			);
		}

		return (
			<div className="row">
				<div className="col-12">
					<Field
						name={this.activeKey}
						component={CustomerCheckboxField}
						type="checkbox"
						label={dayLabel}
						isDisabled={this.isDisabled}
						dontShowCustomer={true}
					/>
				</div>
				{times}
			</div>
		);
	}

	// For single day edit
	public renderAsChoice() {
		const { t } = this.props;

		if (this.isDisabled) {
			return <div className="note">{t('Shop_WorkTime_Error_Closed')}</div>;
		}

		let periods: JSX.Element;

		if (this.isActive) {
			periods = (
				<div className="row">
					<div className="col-12">
						<FieldArray name={this.periodsKey} component={this.renderPeriods} />
					</div>
				</div>
			);
		}

		return (
			<>
				<div>
					<Field
						className="col-sm-6"
						name={this.activeKey}
						component={PortalCheckboxField}
						type="checkbox"
						label={t('Shop_WorkTime_Label_DayOff')}
						inverse={true}
						isDisabled={this.isDisabled}
					/>
					{this.isActive ? null : <div className="note checkbox-note">{t('Shop_WorkTime_Description_DayOff')}</div>}
				</div>

				<div>
					<Field
						name={this.activeKey}
						component={PortalCheckboxField}
						type="checkbox"
						label={t('Shop_WorkTime_Label_CustomTime')}
						inverse={false}
						isDisabled={this.isDisabled}
					/>
				</div>

				{periods}
			</>
		);
	}

	private renderPeriods(
		{ fields }: { fields: FieldArrayFieldsProps<IBookingTimeTablePeriod> },
		meta: FieldArrayMetaProps,
	): JSX.Element {
		const { submitting, t } = this.props;

		return (
			<>
				<div className="periods">{fields.map(this.renderPeriod)}</div>
				{meta.submitFailed &&
					((meta.error && <p className="red">{meta.error}</p>) ||
						(meta.warning && <p className="red">{meta.warning}</p>))}

				<div className="row">
					<div className="col-12 d-flex">
						{!this.canAdd ? null : (
							<div
								className="addAvailability"
								onClick={() => {
									!submitting ? fields.push({ startTime: '', endTime: '', availabilityType: 0 }) : null;
								}}
							>
								{renderIcon('nikaPlus', 20, 20)}
								<span>{t('Shop_WorkTime_AddAvailability')}</span>
							</div>
						)}
					</div>
				</div>
			</>
		);
	}

	private renderPeriod(
		name: string,
		index: number,
		fields: FieldArrayFieldsProps<IBookingTimeTablePeriod>,
	): JSX.Element {
		const { submitting, t, minTime, maxTime, currentFormValues } = this.props;
		const fieldName = name.replace('[', '.').replace(']', '');
		const fieldNameWithoutIndex = fieldName.substring(0, fieldName.lastIndexOf('.') + 1);
		const value: IBookingTimeTablePeriod = getProp(currentFormValues, fieldName);
		const validateStartTime = [required];
		const validateEndTime = [required];
		let minTimeNew: Date = minTime;
		const timezone = this.props.timezone;

		if (value?.startTime) {
			const min = value.startTime;
			const valueMinDate = momentWithTimezone(timezone, min);

			if (valueMinDate) {
				validateEndTime.push(createEndTimeValidationFuntion(min, t, timezone));
			}
		}

		// Set minTime of current workTime period to be the endTime of the previous workTime period
		if (index > 0) {
			const prevFieldName = fieldNameWithoutIndex + `${index - 1}`;
			const prevValue: IBookingTimeTablePeriod = getProp(currentFormValues, prevFieldName);
			minTimeNew = momentWithTimezone(timezone, prevValue.endTime).toDate();
		}

		if (minTime) {
			validateEndTime.push(createEndTimeValidationFuntion(minTimeNew, t, timezone));
			validateStartTime.push(createEndTimeValidationFuntion(minTimeNew, t, timezone, true));
		}

		return (
			<div className="row" key={index}>
				<div className="col-md-5 col-12">
					<Field
						name={name + '.availabilityType'}
						component={BookingSelectMenu}
						type="text"
						items={this.availabilityItems()}
						label={t('Shop_WorkTime_Availability')}
						information={t('Shop_WorkTime_Availability')}
						placeholder={t('Shop_WorkTime_OnlineManual_Booking')}
						isRequired={true}
						searchable={false}
					/>
				</div>

				<div className="col-md-3 col-6 d-flex position-relative workTimeField">
					<Field
						name={name + '.startTime'}
						type="text"
						component={BookingDatePicker}
						label={t('Shop_ManageShop_Location_Label_OpeningTimes_From')}
						validate={validateStartTime}
						{...timePickerProps(minTimeNew, maxTime)}
						timezone={timezone}
					/>

					<span className="minusBetween">-</span>
				</div>

				<div className="col-md-3 col-6 d-flex workTimeField">
					<Field
						name={name + '.endTime'}
						type="text"
						component={BookingDatePicker}
						label={t('Shop_ManageShop_Location_Label_OpeningTimes_To')}
						validate={validateEndTime}
						{...timePickerProps(minTimeNew, maxTime)}
						timezone={timezone}
					/>
				</div>
				<hr />
				<div className="col-md-1 col-12 d-flex justify-content-md-end period-actions">
					<div className="deleteButton d-flex justify-content-md-end">
						<button className="btnIcon round" disabled={submitting} onClick={() => fields.remove(index)}>
							{renderIcon('delete', 24, 24)} <span className="btnIcon__remove">{t('BSPortal_Common_Remove')}</span>
						</button>
					</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: IStore) => {
	let currentFormValues: Partial<IBookingTimeTableForm> = {};

	if (state.form[c.FORM_NAME_WORK_TIME] && state.form[c.FORM_NAME_WORK_TIME].values) {
		currentFormValues = state.form[c.FORM_NAME_WORK_TIME].values;
	}

	return {
		currentFormValues,
	};
};

const WorkDayPeriods = connect(mapStateToProps)(WorkDayPeriodForm);

export { WorkDayPeriods };
